/**
 * Quest cover photos: limits, storage keys, and the crop math shared by the editor
 * (crop + encode in the browser) and the Worker (upload checks + serving).
 */

/** Cover width : height of the encoded image. Must match --qp-cover-ratio in src/style.css (slot and crop frame). */
export const COVER_ASPECT_W = 16;
export const COVER_ASPECT_H = 9;
export const COVER_ASPECT = COVER_ASPECT_W / COVER_ASPECT_H;
/** The encoded cover is at most this wide (never upscaled). */
export const COVER_MAX_WIDTH = 1600;
export const COVER_WEBP_QUALITY = 0.82;
/** A picked file larger than this is refused before decoding. */
export const COVER_MAX_RAW_BYTES = 15 * 1024 * 1024;
/** The encoded upload; a 1600×900 WebP at 0.82 is far below this. */
export const COVER_MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
/** Zoom range in the crop step: 1 = the photo just covers the frame. */
export const COVER_MAX_ZOOM = 4;
/** Published covers: the URL changes with the bytes, so it can be kept for a year. */
export const COVER_PUBLIC_CACHE = "public, max-age=31536000, immutable";
/** Drafts and hidden quests: only the owner (or an admin) sees them, and nothing keeps a copy. */
export const COVER_PRIVATE_CACHE = "private, no-store";

export type CoverExt = "webp" | "jpg";

export const COVER_CONTENT_TYPE: Record<CoverExt, string> = {
  webp: "image/webp",
  jpg: "image/jpeg",
};

/** Why a picked file can't be a cover, or null when it can. */
export function coverFileProblem(file: { type: string; size: number }): "type" | "size" | null {
  if (!/^image\//i.test(file.type)) return "type";
  if (file.size > COVER_MAX_RAW_BYTES) return "size";
  return null;
}

/** What the uploaded bytes really are: WebP (or JPEG where the browser can't encode WebP). */
export function sniffCover(bytes: Uint8Array): CoverExt | null {
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return "webp";
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "jpg";
  return null;
}

const FILE = /^([0-9a-f]{32})\.(webp|jpg)$/;

/** R2 key: covers/<missionId>/<hash>.webp */
export function coverKey(missionId: string, hash: string, ext: CoverExt): string {
  return `covers/${missionId}/${hash}.${ext}`;
}

/** The public route that serves a stored key, or null when the key isn't a cover key. */
export function coverUrl(missionId: string, key: string | null | undefined): string | null {
  if (!key) return null;
  const prefix = `covers/${missionId}/`;
  if (!key.startsWith(prefix)) return null;
  const file = key.slice(prefix.length);
  if (!FILE.test(file)) return null;
  return `/api/covers/${encodeURIComponent(missionId)}/${file}`;
}

/** `<hash>.webp` from a cover URL → its parts, or null. */
export function parseCoverFile(file: string): { hash: string; ext: CoverExt } | null {
  const m = FILE.exec(file);
  return m ? { hash: m[1]!, ext: m[2] as CoverExt } : null;
}

/** The date the cover shows: published quests their publish date, drafts their created date. */
export function coverDate(q: { status: string; created_at: string; published_at?: string | null }): string {
  return q.status !== "draft" && q.published_at ? q.published_at : q.created_at;
}

// ---------- crop ----------

/** The photo inside the crop frame: its top-left (frame px, ≤ 0) and scale (frame px per photo px). */
export interface CoverCrop {
  /** Frame size in screen px (COVER_ASPECT). */
  frameW: number;
  frameH: number;
  /** Photo size in its own px. */
  imageW: number;
  imageH: number;
  /** 1 … COVER_MAX_ZOOM */
  zoom: number;
  x: number;
  y: number;
}

/** Scale at zoom 1: the photo just covers the frame. */
export function coverBaseScale(c: Pick<CoverCrop, "frameW" | "frameH" | "imageW" | "imageH">): number {
  return Math.max(c.frameW / c.imageW, c.frameH / c.imageH);
}

export function coverScale(c: CoverCrop): number {
  return coverBaseScale(c) * c.zoom;
}

/** Keep the frame covered: the photo's edges never come inside it. */
export function clampCrop(c: CoverCrop): CoverCrop {
  const zoom = Math.min(COVER_MAX_ZOOM, Math.max(1, Number.isFinite(c.zoom) ? c.zoom : 1));
  const next = { ...c, zoom };
  const s = coverScale(next);
  const minX = c.frameW - c.imageW * s;
  const minY = c.frameH - c.imageH * s;
  next.x = Math.min(0, Math.max(minX, c.x));
  next.y = Math.min(0, Math.max(minY, c.y));
  return next;
}

/** The photo centered in the frame at zoom 1. */
export function startCrop(frameW: number, frameH: number, imageW: number, imageH: number): CoverCrop {
  const base = { frameW, frameH, imageW, imageH, zoom: 1, x: 0, y: 0 };
  const s = coverScale(base);
  return clampCrop({ ...base, x: (frameW - imageW * s) / 2, y: (frameH - imageH * s) / 2 });
}

export function panCrop(c: CoverCrop, dx: number, dy: number): CoverCrop {
  return clampCrop({ ...c, x: c.x + dx, y: c.y + dy });
}

/** Zoom keeping the photo point under (fx, fy) — frame px, default the frame center — in place. */
export function zoomCrop(c: CoverCrop, zoom: number, fx = c.frameW / 2, fy = c.frameH / 2): CoverCrop {
  const before = coverScale(c);
  const z = Math.min(COVER_MAX_ZOOM, Math.max(1, zoom));
  const after = coverBaseScale(c) * z;
  const px = (fx - c.x) / before;
  const py = (fy - c.y) / before;
  return clampCrop({ ...c, zoom: z, x: fx - px * after, y: fy - py * after });
}

/** The photo rectangle the frame shows, in photo px, and the encoded size. */
export function cropOutput(c: CoverCrop): { sx: number; sy: number; sw: number; sh: number; width: number; height: number } {
  const s = coverScale(c);
  const sw = Math.min(c.imageW, c.frameW / s);
  const sh = Math.min(c.imageH, c.frameH / s);
  const sx = Math.min(c.imageW - sw, Math.max(0, -c.x / s));
  const sy = Math.min(c.imageH - sh, Math.max(0, -c.y / s));
  const width = Math.max(1, Math.round(Math.min(COVER_MAX_WIDTH, sw)));
  const height = Math.max(1, Math.round((width * COVER_ASPECT_H) / COVER_ASPECT_W));
  return { sx, sy, sw, sh, width, height };
}
